(() => {
  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(value);
  };

  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/&/g, 'and').replace(/grey/g, 'gray').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => prefersReducedMotion() ? 'auto' : 'smooth';
  const getFocusable = (container) => Array.from(container?.querySelectorAll?.('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') || []).filter((el) => !el.hidden && el.offsetParent !== null);
  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const init = (root) => {
    if (!root || root.dataset.meAfV12Ready === 'true') return;
    root.dataset.meAfV12Ready = 'true';

    const swatches = Array.from(root.querySelectorAll('[data-me-af-color-swatch]'));
    const previewButtons = Array.from(root.querySelectorAll('[data-me-af-preview-color]'));
    const selectedColorLabel = root.querySelector('[data-me-af-selected-color]');
    const rows = Array.from(root.querySelectorAll('[data-me-af-row]'));
    const inputs = Array.from(root.querySelectorAll('[data-me-af-input]'));
    const tierEls = Array.from(root.querySelectorAll('[data-me-af-tier]'));
    const totalUnitsEl = root.querySelector('[data-me-af-total-units]');
    const originalTotalEl = root.querySelector('[data-me-af-original-total]');
    const savingsEl = root.querySelector('[data-me-af-savings]');
    const estimatedTotalEl = root.querySelector('[data-me-af-estimated-total]');
    const rateLabelEl = root.querySelector('[data-me-af-rate-label]');
    const nextTierEl = root.querySelector('[data-me-af-next-tier]');
    const unitPriceEl = root.querySelector('[data-me-af-unit-price]');
    const clearButtons = Array.from(root.querySelectorAll('[data-me-af-clear]'));
    const addButtons = Array.from(root.querySelectorAll('[data-me-af-add]'));
    const addLabels = Array.from(root.querySelectorAll('[data-me-af-add-label]'));
    const statusEls = Array.from(root.querySelectorAll('[data-me-af-status]'));
    const volumeQuoteWrap = root.querySelector('[data-me-af-volume-quote-wrap]');
    const volumeQuoteButton = root.querySelector('[data-me-af-volume-quote]');
    const orderStatus = root.dataset.orderStatus || 'available';
    const isComingSoon = orderStatus === 'coming-soon';
    const isPreOrder = orderStatus === 'pre-order';
    const isUnavailable = ['coming-soon', 'out-of-stock', 'discontinued'].includes(orderStatus);
    const quoteMin = Number(root.dataset.quoteMin || 100) || 100;
    const quotePath = '/pages/group-order-quote';
    const quoteStorageKey = 'me-group-order-quote-draft';
    const encodeQuoteDraft = (manifest) => {
      try {
        const bytes = new TextEncoder().encode(JSON.stringify(manifest));
        let binary = '';
        bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
      } catch (_) { return ''; }
    };
    const bottomUnitsEls = Array.from(root.querySelectorAll('[data-me-af-bottom-units]'));
    const bottomTotalEls = Array.from(root.querySelectorAll('[data-me-af-bottom-total]'));
    const selectionBreakdown = root.querySelector('[data-me-af-selection-breakdown]');
    const selectionLines = root.querySelector('[data-me-af-selection-lines]');
    const selectionCount = root.querySelector('[data-me-af-selection-count]');
    const gallery = root.querySelector('[data-me-af-gallery]');
    const mainImage = gallery?.querySelector('.me-af-v12-gallery__main');
    const thumbs = gallery ? Array.from(gallery.querySelectorAll('[data-me-af-thumb]')) : [];
    const galleryIndex = gallery?.querySelector('[data-me-af-gallery-index]');
    const galleryCount = gallery?.querySelector('[data-me-af-gallery-count]');
    const galleryPrev = gallery?.querySelector('[data-me-af-gallery-prev]');
    const galleryNext = gallery?.querySelector('[data-me-af-gallery-next]');
    const colorMediaMapNode = root.querySelector('[data-me-af-color-media-map]');
    let colorMediaMap = {};
    try { colorMediaMap = JSON.parse(colorMediaMapNode?.textContent || '{}'); } catch (error) { console.warn('ME AF color media map unavailable', error); }
    // v13.7.0: gallery logic intentionally frozen from stable v13.6.7.
    // v13.6.7 media diagnostics: append ?me_media_debug=1 to a blank-product URL
    // to inspect the exact color -> Front media mapping and active gallery group.
    const mediaDebugEnabled = new URLSearchParams(window.location.search).get('me_media_debug') === '1';
    let visibleThumbs = [];
    let activeThumbIndex = 0;
    let galleryModal = null;
    let galleryModalPreviousFocus = null;
    let sizeChartModalPreviousFocus = null;

    const viewPriority = {
      front: 10,
      side: 20,
      back: 30,
      detail: 40,
      tag: 50,
      lifestyle: 60,
      other: 100
    };

    const sortGalleryThumbs = (items) => items.slice().sort((a, b) => {
      const aPriority = viewPriority[normalize(a.dataset.viewKey)] ?? viewPriority.other;
      const bPriority = viewPriority[normalize(b.dataset.viewKey)] ?? viewPriority.other;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return thumbs.indexOf(a) - thumbs.indexOf(b);
    });

    const colorLibrary = window.ME_COLOR_LIBRARY?.['af-apparel'] || {};
    root.querySelectorAll('[data-me-color-handle]').forEach((element) => {
      const key = normalize(element.dataset.meColorHandle || '');
      const value = colorLibrary[key] || '#d6d2cc';
      if (element.matches('.me-af-v12__swatch')) element.style.setProperty('--me-swatch', value);
      else element.style.setProperty('--me-swatch', value);
    });

    const getDiscountRate = (qty) => {
      if (qty >= 48) return 0.07;
      if (qty >= 24) return 0.05;
      if (qty >= 12) return 0.02;
      return 0;
    };

    const getNextTier = (qty) => {
      if (qty < 12) return { min: 12, rate: 2 };
      if (qty < 24) return { min: 24, rate: 5 };
      if (qty < 48) return { min: 48, rate: 7 };
      if (qty < quoteMin) return { min: quoteMin, quote: true };
      return null;
    };

    const setActiveThumb = (thumb) => {
      if (!thumb || !mainImage) return;
      visibleThumbs = sortGalleryThumbs(thumbs.filter((item) => !item.hidden));
      activeThumbIndex = Math.max(0, visibleThumbs.indexOf(thumb));
      thumbs.forEach((item) => {
        const active = item === thumb;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
      mainImage.classList.add('is-changing');
      const preload = new Image();
      preload.onload = () => {
        mainImage.srcset = '';
        mainImage.src = thumb.dataset.src;
        mainImage.alt = thumb.dataset.alt || '';
        requestAnimationFrame(() => mainImage.classList.remove('is-changing'));
      };
      preload.onerror = () => mainImage.classList.remove('is-changing');
      preload.src = thumb.dataset.src;
      if (galleryIndex) galleryIndex.textContent = String(activeThumbIndex + 1);
      if (galleryCount) galleryCount.textContent = String(visibleThumbs.length || 1);
      thumb.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest', inline: 'center' });
    };

    const filterGallery = (colorKey) => {
      if (!gallery || !thumbs.length) return;
      const normalizedKey = normalize(colorKey);
      const exactMediaId = String(colorMediaMap[colorKey] || colorMediaMap[normalizedKey] || '');
      const exactAnchor = exactMediaId
        ? thumbs.find((thumb) => String(thumb.dataset.mediaId || '') === exactMediaId)
        : null;

      let matches = 0;
      thumbs.forEach((thumb) => {
        const thumbKey = normalize(thumb.dataset.colorKey);
        const isWrongAnchor = thumb.dataset.isAnchor === 'true' && exactMediaId && String(thumb.dataset.mediaId || '') !== exactMediaId;
        const show = !isWrongAnchor && thumbKey && thumbKey === normalizedKey;
        thumb.hidden = !show;
        if (show) matches += 1;
      });

      // The exact Shopify variant media map is authoritative for Front.
      // Always include that exact media item for the selected color, even if
      // legacy ALT data or a prior resolver labeled the thumbnail incorrectly.
      if (exactAnchor) {
        exactAnchor.hidden = false;
        exactAnchor.dataset.colorKey = colorKey;
        exactAnchor.dataset.viewKey = 'front';
        exactAnchor.dataset.isAnchor = 'true';
        matches = thumbs.filter((thumb) => !thumb.hidden).length;
      }

      if (matches === 0) {
        // Safe failure: one exact variant Front if known; never another color
        // and never the complete product-media library.
        thumbs.forEach((thumb) => { thumb.hidden = thumb !== exactAnchor; });
        matches = exactAnchor ? 1 : 0;
      }

      visibleThumbs = sortGalleryThumbs(thumbs.filter((thumb) => !thumb.hidden));
      visibleThumbs.forEach((thumb, index) => { thumb.style.order = String(index); });
      const hasMultiple = visibleThumbs.length > 1;
      if (galleryPrev) { galleryPrev.hidden = !hasMultiple; galleryPrev.disabled = !hasMultiple; }
      if (galleryNext) { galleryNext.hidden = !hasMultiple; galleryNext.disabled = !hasMultiple; }
      const front = exactAnchor && !exactAnchor.hidden
        ? exactAnchor
        : visibleThumbs.find((thumb) => normalize(thumb.dataset.viewKey) === 'front');
      const first = front || visibleThumbs[0];
      if (first) setActiveThumb(first);

      if (mediaDebugEnabled) {
        console.groupCollapsed(`[Moments Etched media] ${colorKey || normalizedKey}`);
        console.log('Normalized color:', normalizedKey);
        console.log('Exact Shopify Front media ID:', exactMediaId || '(none)');
        console.log('Exact Front anchor found:', Boolean(exactAnchor));
        console.table(visibleThumbs.map((thumb, index) => ({
          order: index + 1,
          mediaId: thumb.dataset.mediaId || '',
          colorKey: thumb.dataset.colorKey || '',
          view: thumb.dataset.viewKey || '',
          isAnchor: thumb.dataset.isAnchor || 'false',
          alt: thumb.dataset.alt || ''
        })));
        console.groupEnd();
      }
    };

    const selectColor = (color, colorKey, options = {}) => {
      const normalizedKey = normalize(colorKey || color);
      swatches.forEach((swatch) => {
        const active = normalize(swatch.dataset.colorKey) === normalizedKey;
        swatch.classList.toggle('is-active', active);
        swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (selectedColorLabel) selectedColorLabel.textContent = color;
      rows.forEach((row) => row.classList.toggle('is-previewing', normalize(row.dataset.colorKey) === normalizedKey));
      filterGallery(normalizedKey);

      if (options.scrollToRow) {
        const row = rows.find((item) => normalize(item.dataset.colorKey) === normalizedKey);
        row?.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      }
      if (options.scrollToGallery && gallery) {
        gallery.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      }
    };

    const clampInventory = (input, requestedValue, announce = true) => {
      const requested = Math.max(0, parseInt(requestedValue || '0', 10) || 0);
      const limitRaw = input.dataset.inventoryLimit;
      if (limitRaw === undefined || limitRaw === '') return requested;
      const limit = Math.max(0, parseInt(limitRaw, 10) || 0);
      const clamped = Math.min(requested, limit);
      const card = input.closest('.me-af-v12__size-card');
      const note = card?.querySelector('[data-me-af-inventory-note]');
      if (note) {
        if (announce && requested > limit) {
          note.textContent = `Only ${limit} currently available for ${input.dataset.color || 'this color'} ${input.dataset.size || ''}.`;
          note.hidden = false;
        } else {
          note.textContent = '';
          note.hidden = true;
        }
      }
      return clamped;
    };

    const recalc = () => {
      let totalQty = 0;
      let originalCents = 0;

      rows.forEach((row) => {
        let rowQty = 0;
        let rowCents = 0;
        row.querySelectorAll('[data-me-af-input]').forEach((input) => {
          const qty = Math.max(0, parseInt(input.value || '0', 10) || 0);
          const price = parseInt(input.dataset.priceCents || '0', 10) || 0;
          rowQty += qty;
          rowCents += qty * price;
        });
        row.querySelector('[data-me-af-row-qty]')?.replaceChildren(document.createTextNode(String(rowQty)));
        row.querySelector('[data-me-af-row-total]')?.replaceChildren(document.createTextNode(money(rowCents)));
        totalQty += rowQty;
        originalCents += rowCents;
      });

      const rate = getDiscountRate(totalQty);
      const savingsCents = Math.round(originalCents * rate);
      const estimatedCents = originalCents - savingsCents;
      const unitCents = totalQty > 0 ? Math.round(estimatedCents / totalQty) : 0;
      const quoteEligible = totalQty >= quoteMin;

      // v13.7.0 — compact order-review breakdown. This is derived only from
      // the existing quantity inputs and does not alter variants, pricing, or gallery behavior.
      const selectedLines = inputs
        .map((input) => ({
          qty: Math.max(0, parseInt(input.value || '0', 10) || 0),
          color: input.dataset.color || '',
          size: input.dataset.size || '',
          price: parseInt(input.dataset.priceCents || '0', 10) || 0
        }))
        .filter((line) => line.qty > 0);
      if (selectionBreakdown && selectionLines) {
        selectionBreakdown.hidden = selectedLines.length === 0;
        selectionLines.replaceChildren(...selectedLines.map((line) => {
          const item = document.createElement('div');
          item.className = 'me-af-v12__selection-line';
          const label = document.createElement('span');
          label.textContent = `${line.color} · ${line.size}`;
          const qty = document.createElement('strong');
          qty.textContent = `${line.qty} × ${money(line.price)}`;
          const total = document.createElement('strong');
          total.className = 'me-af-v12__selection-line-total';
          total.textContent = money(line.qty * line.price);
          item.append(label, qty, total);
          return item;
        }));
        if (selectionCount) selectionCount.textContent = `${selectedLines.length} line item${selectedLines.length === 1 ? '' : 's'}`;
      }

      if (totalUnitsEl) totalUnitsEl.textContent = String(totalQty);
      if (originalTotalEl) originalTotalEl.textContent = money(originalCents);
      if (savingsEl) savingsEl.textContent = `−${money(savingsCents)}`;
      if (estimatedTotalEl) estimatedTotalEl.textContent = money(estimatedCents);
      if (unitPriceEl) unitPriceEl.textContent = totalQty > 0 ? `${money(unitCents)} each` : '—';
      if (rateLabelEl) rateLabelEl.textContent = `(${Math.round(rate * 100)}%)`;
      clearButtons.forEach((button) => { button.disabled = totalQty === 0; });

      let activeMin = 1;
      if (totalQty >= quoteMin) activeMin = quoteMin;
      else if (totalQty >= 48) activeMin = 48;
      else if (totalQty >= 24) activeMin = 24;
      else if (totalQty >= 12) activeMin = 12;
      tierEls.forEach((tier) => tier.classList.toggle('is-active', Number(tier.dataset.min) === activeMin));

      const next = getNextTier(totalQty);
      if (nextTierEl) {
        if (!next) nextTierEl.textContent = `7% checkout pricing remains available. You can also request a volume quote if your order is not time-sensitive.`;
        else if (next.quote) nextTierEl.textContent = `Add ${next.min - totalQty} more item${next.min - totalQty === 1 ? '' : 's'} to unlock the optional 100+ volume quote review.`;
        else nextTierEl.textContent = `Add ${next.min - totalQty} more item${next.min - totalQty === 1 ? '' : 's'} to unlock ${next.rate}% savings.`;
      }

      addButtons.forEach((button) => {
        button.disabled = isUnavailable || totalQty === 0;
        button.classList.remove('is-quote');
      });
      addLabels.forEach((label) => {
        if (orderStatus === 'coming-soon') label.textContent = 'COMING SOON';
        else if (orderStatus === 'out-of-stock') label.textContent = 'OUT OF STOCK';
        else if (orderStatus === 'discontinued') label.textContent = 'DISCONTINUED';
        else if (isPreOrder) label.textContent = totalQty > 0 ? `PRE-ORDER ${totalQty} ITEM${totalQty === 1 ? '' : 'S'}` : 'SELECT ITEMS TO PRE-ORDER';
        else label.textContent = totalQty > 0 ? `ADD ${totalQty} ITEM${totalQty === 1 ? '' : 'S'} TO CART` : 'ADD SELECTED ITEMS TO CART';
      });
      if (volumeQuoteWrap) volumeQuoteWrap.hidden = !quoteEligible;
      if (volumeQuoteButton) volumeQuoteButton.disabled = !quoteEligible || isUnavailable;
      bottomUnitsEls.forEach((el) => { el.textContent = String(totalQty); });
      bottomTotalEls.forEach((el) => { el.textContent = money(estimatedCents); });
    };

    const changeInput = (button, delta) => {
      const wrap = button.closest('[data-me-af-qty]');
      const input = wrap?.querySelector('[data-me-af-input]');
      if (!input || input.disabled) return;
      const current = Math.max(0, parseInt(input.value || '0', 10) || 0);
      input.value = String(clampInventory(input, current + delta, true));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const ensureGalleryModal = () => {
      if (galleryModal) return galleryModal;
      galleryModal = document.createElement('div');
      galleryModal.className = 'me-af-v12-gallery-modal';
      galleryModal.dataset.meAfGalleryModal = '';
      galleryModal.hidden = true;
      galleryModal.innerHTML = `
        <div class="me-af-v12-gallery-modal__dialog" role="dialog" aria-modal="true" aria-label="Product image viewer">
          <button type="button" class="me-af-v12-gallery-modal__close" data-me-af-gallery-modal-close aria-label="Close image viewer">×</button>
          <button type="button" class="me-af-v12-gallery-modal__arrow me-af-v12-gallery-modal__arrow--prev" data-me-af-gallery-modal-prev aria-label="Previous image">‹</button>
          <img class="me-af-v12-gallery-modal__image" alt="">
          <button type="button" class="me-af-v12-gallery-modal__arrow me-af-v12-gallery-modal__arrow--next" data-me-af-gallery-modal-next aria-label="Next image">›</button>
          <div class="me-af-v12-gallery-modal__counter"><span data-me-af-gallery-modal-index>1</span> / <span data-me-af-gallery-modal-count>1</span></div>
        </div>`;
      document.body.appendChild(galleryModal);

      const close = () => {
        galleryModal.hidden = true;
        document.body.style.overflow = '';
        if (galleryModalPreviousFocus?.focus) galleryModalPreviousFocus.focus();
      };
      const render = () => {
        const thumb = visibleThumbs[activeThumbIndex];
        if (!thumb) return;
        const image = galleryModal.querySelector('.me-af-v12-gallery-modal__image');
        image.src = thumb.dataset.fullSrc || thumb.dataset.src;
        image.alt = thumb.dataset.alt || 'Product image';
        galleryModal.querySelector('[data-me-af-gallery-modal-index]').textContent = String(activeThumbIndex + 1);
        galleryModal.querySelector('[data-me-af-gallery-modal-count]').textContent = String(visibleThumbs.length || 1);
      };
      const move = (delta) => {
        if (!visibleThumbs.length) return;
        activeThumbIndex = (activeThumbIndex + delta + visibleThumbs.length) % visibleThumbs.length;
        setActiveThumb(visibleThumbs[activeThumbIndex]);
        render();
      };

      galleryModal.addEventListener('click', (event) => {
        if (event.target === galleryModal || event.target.closest('[data-me-af-gallery-modal-close]')) close();
        else if (event.target.closest('[data-me-af-gallery-modal-prev]')) move(-1);
        else if (event.target.closest('[data-me-af-gallery-modal-next]')) move(1);
      });
      document.addEventListener('keydown', (event) => {
        if (galleryModal.hidden) return;
        trapFocus(galleryModal, event);
        if (event.key === 'Escape') close();
        else if (event.key === 'ArrowLeft') move(-1);
        else if (event.key === 'ArrowRight') move(1);
      });
      galleryModal._renderCurrent = render;
      return galleryModal;
    };

    const openGalleryModal = () => {
      if (!visibleThumbs.length) return;
      const modal = ensureGalleryModal();
      galleryModalPreviousFocus = document.activeElement;
      modal._renderCurrent?.();
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => modal.querySelector('[data-me-af-gallery-modal-close]')?.focus());
    };

    const openSizeChartModal = () => {
      const source = root.querySelector('.me-af-v12__size-chart-frame img');
      if (!source) return;
      let modal = document.querySelector('[data-me-af-size-chart-modal]');
      if (!modal) {
        modal = document.createElement('div');
        modal.className = 'me-af-v12__size-chart-modal';
        modal.dataset.meAfSizeChartModal = '';
        modal.hidden = true;
        modal.innerHTML = `
          <div class="me-af-v12__size-chart-modal-inner" role="dialog" aria-modal="true" aria-label="Size chart">
            <button type="button" class="me-af-v12__size-chart-modal-close" data-me-af-size-chart-close aria-label="Close size chart">×</button>
            <img alt="">
          </div>`;
        document.body.appendChild(modal);
        const closeSizeChart = () => {
          modal.hidden = true;
          document.body.style.overflow = '';
          if (sizeChartModalPreviousFocus?.focus) sizeChartModalPreviousFocus.focus();
        };
        modal.addEventListener('click', (e) => {
          if (e.target === modal || e.target.closest('[data-me-af-size-chart-close]')) closeSizeChart();
        });
        document.addEventListener('keydown', (e) => {
          if (modal.hidden) return;
          trapFocus(modal, e);
          if (e.key === 'Escape') closeSizeChart();
        });
        modal._close = closeSizeChart;
      }
      const img = modal.querySelector('img');
      img.src = source.currentSrc || source.src;
      img.alt = source.alt || 'Size chart';
      sizeChartModalPreviousFocus = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => modal.querySelector('[data-me-af-size-chart-close]')?.focus());
    };

    const activateTab = (tab, focus = false) => {
      if (!tab) return;
      const key = tab.dataset.meAfTab;
      const tabs = Array.from(root.querySelectorAll('[data-me-af-tab]'));
      tabs.forEach((button) => {
        const active = button === tab;
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });
      root.querySelectorAll('[data-me-af-panel]').forEach((panel) => { panel.hidden = panel.dataset.meAfPanel !== key; });
      if (focus) tab.focus();
    };

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-me-af-gallery-open], .me-af-v12-gallery__main')) {
        event.preventDefault();
        openGalleryModal();
        return;
      }

      const sizeChartOpen = event.target.closest('[data-me-af-size-chart-open]');
      if (sizeChartOpen) {
        event.preventDefault();
        openSizeChartModal();
        return;
      }

      const swatch = event.target.closest('[data-me-af-color-swatch]');
      if (swatch) {
        event.preventDefault();
        selectColor(swatch.dataset.color, swatch.dataset.colorKey, { scrollToRow: true });
        return;
      }

      const preview = event.target.closest('[data-me-af-preview-color]');
      if (preview) {
        event.preventDefault();
        selectColor(preview.dataset.color, preview.dataset.colorKey, { scrollToGallery: window.matchMedia('(max-width: 1179px)').matches });
        return;
      }

      const thumb = event.target.closest('[data-me-af-thumb]');
      if (thumb && !thumb.hidden) {
        event.preventDefault();
        setActiveThumb(thumb);
        return;
      }

      const minus = event.target.closest('[data-me-af-minus]');
      if (minus) { event.preventDefault(); changeInput(minus, -1); return; }
      const plus = event.target.closest('[data-me-af-plus]');
      if (plus) { event.preventDefault(); changeInput(plus, 1); return; }

      const tab = event.target.closest('[data-me-af-tab]');
      if (tab) {
        event.preventDefault();
        activateTab(tab);
      }
    });

    root.addEventListener('keydown', (event) => {
      const tab = event.target.closest?.('[data-me-af-tab]');
      if (tab && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
        const tabs = Array.from(root.querySelectorAll('[data-me-af-tab]'));
        const current = Math.max(0, tabs.indexOf(tab));
        let next = current;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        event.preventDefault();
        activateTab(tabs[next], true);
        return;
      }

      const swatch = event.target.closest?.('[data-me-af-color-swatch]');
      if (swatch && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) {
        const current = Math.max(0, swatches.indexOf(swatch));
        let next = current;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % swatches.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + swatches.length) % swatches.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = swatches.length - 1;
        event.preventDefault();
        const target = swatches[next];
        target.focus();
        selectColor(target.dataset.color, target.dataset.colorKey);
      }
    });

    root.addEventListener('input', (event) => {
      if (!event.target.matches('[data-me-af-input]')) return;
      const value = Math.max(0, parseInt(event.target.value || '0', 10) || 0);
      event.target.value = String(clampInventory(event.target, value, true));
      recalc();
    });

    galleryPrev?.addEventListener('click', () => {
      if (!visibleThumbs.length) return;
      activeThumbIndex = (activeThumbIndex - 1 + visibleThumbs.length) % visibleThumbs.length;
      setActiveThumb(visibleThumbs[activeThumbIndex]);
    });
    galleryNext?.addEventListener('click', () => {
      if (!visibleThumbs.length) return;
      activeThumbIndex = (activeThumbIndex + 1) % visibleThumbs.length;
      setActiveThumb(visibleThumbs[activeThumbIndex]);
    });

    const clearSelections = () => {
      inputs.forEach((input) => {
        if (!input.disabled) input.value = '0';
        const note = input.closest('.me-af-v12__size-card')?.querySelector('[data-me-af-inventory-note]');
        if (note) { note.textContent = ''; note.hidden = true; }
      });
      statusEls.forEach((status) => { status.textContent = ''; status.classList.remove('is-error'); });
      recalc();
    };

    const makeQuoteReference = () => {
      const now = new Date();
      const date = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('');
      const random = Math.random().toString(36).slice(2, 7).toUpperCase();
      return `MEQ-${date}-${random}`;
    };

    const requestVolumeQuote = () => {
      const productTitle = document.querySelector('.me-af-v12__title')?.textContent?.trim() || document.title;
      const selections = inputs.map((input) => {
        const quantity = Math.max(0, parseInt(input.value || '0', 10) || 0);
        const unitPriceCents = Number(input.dataset.priceCents || 0);
        return {
          variantId: Number(input.dataset.variantId),
          productTitle,
          color: input.dataset.color || '',
          size: input.dataset.size || '',
          quantity,
          unitPriceCents,
          lineOriginalCents: quantity * unitPriceCents,
          unitPrice: money(unitPriceCents)
        };
      }).filter((item) => item.variantId && item.quantity > 0);
      const totalQty = selections.reduce((sum, item) => sum + item.quantity, 0);
      if (totalQty < quoteMin) return;
      const originalCents = selections.reduce((sum, item) => {
        const source = inputs.find((input) => Number(input.dataset.variantId) === item.variantId && input.dataset.color === item.color && input.dataset.size === item.size);
        return sum + item.quantity * Number(source?.dataset.priceCents || 0);
      }, 0);
      const rate = getDiscountRate(totalQty);
      const savingsCents = Math.round(originalCents * rate);
      const estimatedCents = originalCents - savingsCents;
      const colors = [...new Set(selections.map((item) => item.color).filter(Boolean))];
      const sizes = [...new Set(selections.map((item) => item.size).filter(Boolean))];
      const reference = makeQuoteReference();
      const manifest = {
        schemaVersion: '1.5',
        sourceType: 'blank-wholesale',
        quoteReference: reference,
        createdAt: new Date().toISOString(),
        responseWindow: '24–48 business hours',
        sourceUrl: window.location.href,
        product: {
          title: productTitle,
          handle: root.dataset.productHandle || '',
          color: colors.length === 1 ? colors[0] : `${colors.length} colors selected`,
          size: sizes.length === 1 ? sizes[0] : `${sizes.length} sizes selected`,
          quantity: totalQty
        },
        selections,
        pricingEstimate: {
          originalTotal: money(originalCents),
          discount: `${Math.round(rate * 100)}% planning estimate; final 100+ volume pricing pending review`,
          savings: money(savingsCents),
          estimatedTotal: money(estimatedCents),
          unitPrice: totalQty ? money(Math.round(estimatedCents / totalQty)) : 'Not available'
        },
        placements: [],
        artwork: []
      };
      try {
        localStorage.setItem(quoteStorageKey, JSON.stringify(manifest));
        sessionStorage.setItem(quoteStorageKey, JSON.stringify(manifest));
        const draft = encodeQuoteDraft(manifest);
        const draftParam = draft && draft.length < 6500 ? `&draft=${encodeURIComponent(draft)}` : '';
        window.location.assign(`${quotePath}?quote=${encodeURIComponent(reference)}${draftParam}`);
      } catch (error) {
        statusEls.forEach((status) => { status.textContent = 'We could not prepare the volume quote. Please try again.'; status.classList.add('is-error'); });
      }
    };

    const addSelectedItems = async () => {
      if (isUnavailable) return;
      const currentQty = inputs.reduce((sum, input) => sum + Math.max(0, parseInt(input.value || '0', 10) || 0), 0);
      const items = inputs.map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: Math.max(0, parseInt(input.value || '0', 10) || 0)
      })).filter((item) => item.id && item.quantity > 0);

      if (!items.length) return;
      addButtons.forEach((button) => { button.classList.add('is-loading'); button.disabled = true; });
      statusEls.forEach((status) => { status.textContent = isPreOrder ? 'Adding selected pre-order items…' : 'Adding selected blank items…'; status.classList.remove('is-error'); });

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add selected items.');
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (error) {
        statusEls.forEach((status) => { status.textContent = error.message || 'Unable to add selected items.'; status.classList.add('is-error'); });
        addButtons.forEach((button) => { button.classList.remove('is-loading'); button.disabled = false; });
      }
    };

    addButtons.forEach((button) => button.addEventListener('click', addSelectedItems));
    volumeQuoteButton?.addEventListener('click', requestVolumeQuote);
    clearButtons.forEach((button) => button.addEventListener('click', clearSelections));

    const initialColor = root.dataset.selectedColor || swatches[0]?.dataset.color || '';
    const initialKey = root.dataset.selectedColorKey || swatches[0]?.dataset.colorKey || normalize(initialColor);
    selectColor(initialColor, initialKey);
    recalc();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-me-af-v12]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
